import { restClient } from '@/Core'
import { userManager } from '@/Core/Managers'

import { TRANSPORT_OFFER_DEFAULT_CURRENCY } from '../Contracts/ITransportOffer'

/** Те, що задача бере з ядрового `formContext` форми пропозиції. */
export interface OfferFormContextLike {
    isEditable?: boolean
    values?: Record<string, unknown>
    setInitialValue?: (path: string, value: unknown) => void
}

interface CurrencyRef {
    '@id'?: string
    uuid?: string
    code?: string
}

const isBlank = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

let pendingCurrency: Promise<CurrencyRef | null> | null = null

/**
 * EUR — `GET /services/currencies/EUR` (ядро адресує валюту кодом). Один запит
 * на сесію; невдалий не кешуємо — наступна форма спробує знову.
 */
const loadDefaultCurrency = (): Promise<CurrencyRef | null> => {
    pendingCurrency ??= (async () => {
        try {
            const currency = (await restClient.get(
                `/services/currencies/${TRANSPORT_OFFER_DEFAULT_CURRENCY}`
            )) as unknown as CurrencyRef | null

            return currency && typeof currency === 'object' ? currency : null
        } catch {
            pendingCurrency = null

            return null
        }
    })()

    return pendingCurrency
}

/** Поточний користувач — значенням select-а (ядро впізнає вибране за `@id`). */
const currentUserRef = (): Record<string, unknown> | null => {
    const user = userManager.user as Record<string, unknown>
    const uuid = typeof user?.['uuid'] === 'string' ? user['uuid'] : null

    if (!uuid) return null

    return { ...user, '@id': typeof user['@id'] === 'string' ? user['@id'] : `/services/users/${uuid}`, uuid }
}

/**
 * Значення форми нової пропозиції (`SPEC-024` §5.1, §8.2):
 *
 *  - **«Автор пропозиції» — той, хто створює** (ПР-02, легасі `OfferCrud.jsx:11`);
 *  - **валюта — EUR** (ПР-04; той самий дефолт ставить і бек).
 *
 * Пише лише в порожні поля й через `setInitialValue`: дефолт не рахується
 * незбереженою зміною.
 */
export const ApplyOfferCreateDefaultsTask = async (formContext: OfferFormContextLike): Promise<void> => {
    if (isBlank(formContext.values?.['responsible'])) {
        const user = currentUserRef()

        if (user) formContext.setInitialValue?.('responsible', user)
    }

    if (!isBlank(formContext.values?.['offeredPriceCurrency'])) return

    const currency = await loadDefaultCurrency()

    if (currency && isBlank(formContext.values?.['offeredPriceCurrency'])) {
        formContext.setInitialValue?.('offeredPriceCurrency', currency)
    }
}
